/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: Idiomatic React patterns mis-flagged by experimental
 * react-hooks rules (set-state inside effect / handler, derived
 * values inside useMemo). All audited.
 */
// v77.99.42 — VoiceDictateButton (prominent redesign)
//
// Browser-native Web Speech API dictation. Per Phase 2 user choice:
// NO server-side Whisper/OpenAI. Free, privacy-preserving (audio
// never leaves the browser), zero LLM cost. Trade-off is browser
// support — works in Chromium-based browsers, recent Safari and
// the iOS PWA shell; gracefully self-hides on Firefox / older
// browsers that don't expose the API.
//
// Visual contract:
//   IDLE      — prominent teal pill with mic icon + label
//                "Tap to speak". Subtle pulsing glow ring so users
//                notice it exists on a busy form.
//   RECORDING — red pill, pulsing red dot, animated equaliser bars,
//                live "Recording 0:12" timer, clear "Stop" label.
//                Visually nothing like the idle state so the user
//                instantly knows the recording is active.
//   UNAVAILABLE — small grey "Voice not supported" chip with mic-off
//                icon (graceful degradation on Firefox / old Safari).

import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square } from "lucide-react";
import { toast } from "sonner";

function getRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function fmtTimer(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec < 10 ? "0" : ""}${sec}`;
}

export function VoiceDictateButton({ onTranscript, lang = "en-GB", disabled = false, testidPrefix = "journal-voice", label = "Tap to speak" }) {
  const Ctor = getRecognitionCtor();
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [seconds, setSeconds] = useState(0);

  // Timer ticks while recording is active.
  useEffect(() => {
    if (!listening) {
      setSeconds(0);
      return;
    }
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [listening]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      try { recRef.current?.stop?.(); } catch { /* noop */ }
    };
  }, []);

  if (!Ctor) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10.5px] text-[#6F828A] px-2 py-1 border border-[#D9E1E3] rounded-sm bg-[#F4FAFA]"
        data-testid={`${testidPrefix}-unsupported`}
        title="Voice dictation needs a Chromium-based browser or recent Safari."
      >
        <MicOff className="w-3.5 h-3.5" strokeWidth={1.6} /> Voice not supported
      </span>
    );
  }

  const start = () => {
    if (listening || disabled) return;
    const r = new Ctor();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (ev) => {
      let chunk = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) chunk += res[0].transcript;
      }
      if (chunk) onTranscript?.(chunk.trim() + " ");
    };
    r.onerror = (ev) => {
      const reason = ev.error || "error";
      if (reason === "not-allowed" || reason === "service-not-allowed") {
        toast.error("Microphone permission denied. Enable mic access in your browser settings.");
      } else if (reason === "no-speech") {
        toast.info("No speech detected — try again.");
      } else if (reason !== "aborted") {
        toast.error(`Voice error: ${reason}`);
      }
      setListening(false);
    };
    r.onend = () => setListening(false);
    try {
      r.start();
      recRef.current = r;
      setListening(true);
    } catch {
      toast.error("Couldn't start voice dictation.");
    }
  };

  const stop = () => {
    try { recRef.current?.stop?.(); } catch { /* noop */ }
    setListening(false);
  };

  // ----- RECORDING STATE: red pill, big stop button, timer + bars -----
  if (listening) {
    return (
      <button
        type="button"
        onClick={stop}
        data-testid={`${testidPrefix}-button`}
        aria-pressed="true"
        aria-label="Stop recording"
        className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-full border-2 border-[#C0291D] bg-[#FBEFED] text-[#8C2A22] shadow-sm transition-all"
      >
        {/* Pulsing red dot */}
        <span className="relative inline-flex w-2.5 h-2.5">
          <span className="absolute inset-0 rounded-full bg-[#C0291D] animate-ping opacity-75" />
          <span className="relative w-2.5 h-2.5 rounded-full bg-[#C0291D]" />
        </span>
        {/* Animated equaliser bars */}
        <span className="inline-flex items-end gap-[2px] h-4" aria-hidden="true">
          <span className="w-[2px] bg-[#C0291D] rounded-sm voice-bar voice-bar-1" />
          <span className="w-[2px] bg-[#C0291D] rounded-sm voice-bar voice-bar-2" />
          <span className="w-[2px] bg-[#C0291D] rounded-sm voice-bar voice-bar-3" />
        </span>
        <span className="tabular-nums" data-testid={`${testidPrefix}-timer`}>{fmtTimer(seconds)}</span>
        <span className="hidden xs:inline">Recording…</span>
        <span className="inline-flex items-center gap-1 ml-1 px-2 py-1 rounded-full bg-[#C0291D] text-white">
          <Square className="w-3 h-3 fill-white" strokeWidth={0} />
          <span className="text-xs font-medium">Stop</span>
        </span>
      </button>
    );
  }

  // ----- IDLE STATE: prominent teal pill with subtle pulsing glow ring -----
  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      data-testid={`${testidPrefix}-button`}
      aria-pressed="false"
      aria-label="Start voice dictation"
      title="Tap and start talking — your voice becomes text you can edit before saving."
      className="relative inline-flex items-center gap-1.5 sm:gap-2 text-sm font-medium px-3 sm:px-3.5 py-2 rounded-full border-2 border-[#116B6E] bg-[#E0F2F2] text-[#0F5658] hover:bg-[#CCE9EA] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {/* Subtle ambient glow ring so the button stands out on a busy form */}
      <span aria-hidden="true" className="absolute inset-0 rounded-full border-2 border-[#116B6E]/30 animate-voice-idle pointer-events-none" />
      <Mic className="w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={2} />
      <span>{label}</span>
    </button>
  );
}
